<template>
  <view-container :loading="loading">
    <div class="flex justify-between flex-wrap gap-2 mb-6">
      <h3 class="font-bold">Reporting</h3>

      <div class="join ml-2">
        <button class="btn btn-sm btn-neutral join-item" @click="goBackwards"> &lt;</button>
        <span class="btn btn-sm btn-neutral join-item">{{ formatDatetime(now, 'MMMM YYYY') }}</span>
        <button class="btn btn-sm btn-neutral join-item" @click="goForward" :disabled="isSameMonth"> &gt;</button>
      </div>
    </div>
    <div>
      <div class="table">
        <div
          class="row"
          v-for="(day, key) in dayReports"
          :key="key"
        >
          <div class="row-header" :class="{ today: isToday(day.date) }">
            <div class="flex gap-4">
              <span class="date">
                <span class="day-name">{{ formatDatetime(day.date, 'ddd') }}</span>
                {{ formatDatetime(day.date, 'D. M. YYYY') }}
              </span>
              <span class="px-2 py-1 bg-neutral-700 rounded text-xs font-semibold" v-if="day.reports.length">
                {{ formatMinutes(sumBy(day.reports, 'duration')) }}
              </span>
              <button class="btn btn-primary btn-xs" @click="createNewReport(day)">Pridať záznam</button>
            </div>
          </div>
          <div class="row-reports" v-if="day.reports.length">
            <div
              class="row-report group"
              v-for="report in sortDayReports(day.reports)"
              :key="'report'+report.id"
              @click="editReport(day, report)"
            >
              <div class="flex-1">
                <!-- Header: Duration and Subject -->
                <div class="flex items-start gap-3 mb-2">
                  <span
                    class="px-3 py-1 bg-neutral-700 rounded-md text-sm font-bold whitespace-nowrap flex-shrink-0"
                    :class="{ 'bg-warning bg-opacity-20 text-warning': report.duration / 60 > 12 }"
                  >
                    {{ formatMinutes(report.duration) }}
                  </span>
                  <div class="text-base font-semibold flex-1">
                    <span v-if="report.subject.parent" class="opacity-60">{{ report.subject.parent.name }} › </span>
                    {{ report.subject.name }}
                  </div>
                </div>

                <!-- Note -->
                <p class="whitespace-pre-wrap text-sm leading-relaxed opacity-70">{{ report.note }}</p>

                <!-- Tags -->
                <div class="flex flex-wrap gap-1.5 mt-3" v-if="report.tags && report.tags.length">
                  <span
                    class="inline-flex items-center px-2.5 py-1 rounded text-xs font-medium"
                    style="background-color: rgba(117, 59, 189, 0.15); color: #b794f6;"
                    v-for="tag in report.tags"
                    :key="'tag'+tag.id"
                  >
                    {{ tag.name }}
                  </span>
                </div>
              </div>

              <!-- Right side: Company, Location, and Type -->
              <div class="flex flex-col items-end gap-2 ml-6 flex-shrink-0 pt-1">
                <div class="flex flex-wrap gap-2">
                  <span class="px-2 py-1 rounded text-xs font-medium" :class="report.type.color">
                    {{ report.type.name }}
                  </span>
                  <span
                      class="px-2 py-1 rounded text-xs font-medium opacity-60 group-hover:opacity-100 transition-opacity"
                      :style="{ backgroundColor: report.company.color + '40', color: report.company.color }"
                    >
                    {{ report.company.code }}
                  </span>
                </div>

                <span class="px-2 py-1 bg-neutral-700 rounded text-xs" v-if="report.location">
                  {{ report.location.name }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="row-footer" v-if="allReports.length">
          <span class="flex gap-2">
              <div class="badge" v-for="item in summary" :key="'summary' + item.id" :style="{ backgroundColor: item.company.color, borderColor: item.company.color }">
                <b>{{ item.company.code }}: </b>
                <span>
                  {{ formatMinutes(item.duration) }}
                  ({{ item.percentage }} %)
                </span>
              </div>
          </span>
          <span class="badge badge-neutral">{{ formatMinutes(allDuration) }}</span>
        </div>
      </div>
    </div>

    <modal ref="modal" @closed="closeModal" size="w-11/12 max-w-2xl" :middle="false">
      <template v-if="selectedDay">
        <h3 class="font-bold text-lg mb-6" v-if="selectedDay">
          {{ isEditingReport ? 'Upraviť záznam' : 'Pridať nový záznam' }}
          <span class="text-primary">{{ formatDatetime(selectedDay.date, 'dd') }}</span>
          {{ formatDatetime(selectedDay.date, 'D. M. YYYY') }}
        </h3>
        <div class="body">
          <form @submit.prevent="formAction" class="space-y-4">
            <!-- Time Section -->
            <div class="card bg-base-200">
              <div class="card-body p-4">
                <div class="flex items-center gap-2 mb-3">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h4 class="font-semibold">Pracovný čas</h4>
                </div>
                <div class="flex flex-wrap gap-3 items-center">
                  <div class="flex gap-2 items-center">
                    <input
                      class="input input-bordered w-20 text-center text-lg font-semibold [appearance:textfield]"
                      type="number"
                      v-model="createReportData.hours"
                      min="0"
                      placeholder="0"
                      required
                    />
                    <span class="text-sm opacity-70">hod</span>
                    <input
                      class="input input-bordered w-20 text-center text-lg font-semibold [appearance:textfield]"
                      type="number"
                      v-model="createReportData.minutes"
                      min="0"
                      max="59"
                      placeholder="0"
                      required
                    />
                    <span class="text-sm opacity-70">min</span>
                  </div>
                  <div class="flex gap-1 flex-wrap">
                    <button type="button" @click="addDuration(5)" class="btn btn-sm btn-neutral">+5</button>
                    <button type="button" @click="addDuration(15)" class="btn btn-sm btn-neutral">+15</button>
                    <button type="button" @click="addDuration(30)" class="btn btn-sm btn-neutral">+30</button>
                    <button type="button" @click="addDuration(60)" class="btn btn-sm btn-neutral">+1h</button>
                    <button type="button" @click="createReportData.hours = createReportData.minutes = 0" class="btn btn-sm btn-ghost" title="Reset">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Subject Section -->
            <div class="card bg-base-200">
              <div class="card-body p-4">
                <div class="flex items-center gap-2 mb-3">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <h4 class="font-semibold">Predmet</h4>
                </div>
                <autocomplete
                  v-model="createReportData.subject_id"
                  :options="reportSubjects"
                  placeholder="Začnite písať názov predmetu..."
                  :required="true"
                />
                <div v-if="mostUsedMySubjects && isCreatingNewReport && mostUsedMySubjects.length" class="mt-3 flex flex-wrap items-center gap-2">
                  <span class="text-xs font-medium opacity-60">Moje top:</span>
                  <button
                    v-for="subject in mostUsedMySubjects"
                    :key="'subject'+subject.id"
                    type="button"
                    @click="applySubject(subject.id)"
                    class="inline-flex items-center px-2.5 py-1 text-xs font-medium bg-white bg-opacity-10 hover:bg-opacity-20 hover:bg-primary rounded-full transition-all border border-white border-opacity-10"
                  >
                    <span v-if="subject.parent" class="opacity-50 mr-1">{{ subject.parent.name }} »</span>
                    <span>{{ subject.name }}</span>
                  </button>
                </div>
              </div>
            </div>

            <!-- Note Section -->
            <div class="card bg-base-200">
              <div class="card-body p-4">
                <div class="flex items-center gap-2 mb-3">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  <h4 class="font-semibold">Poznámka</h4>
                </div>
                <textarea
                  ref="note"
                  class="textarea textarea-bordered min-h-[80px]"
                  v-model="createReportData.note"
                  minlength="5"
                  placeholder="Čo si robil..."
                  @focus="resizeNoteTextarea"
                  @input="resizeNoteTextarea"
                  required
                ></textarea>
              </div>
            </div>

            <!-- Tags Section -->
            <div class="card bg-base-200">
              <div class="card-body p-4">
                <div class="flex items-center gap-2 mb-3">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  <h4 class="font-semibold">Tagy</h4>
                </div>
                <tag-autocomplete
                  v-model="createReportData.tags"
                  :options="reportTags"
                  placeholder="Začnite písať tag..."
                />
                <div v-if="mostUsedMyTags && isCreatingNewReport && mostUsedMyTags.length" class="mt-3 flex flex-wrap items-center gap-2">
                  <span class="text-xs font-medium opacity-60">Moje top:</span>
                  <button
                    v-for="tag in mostUsedMyTags"
                    :key="'tag'+tag.id"
                    type="button"
                    @click="applyTag(tag.id)"
                    class="inline-flex items-center px-2.5 py-1 text-xs font-medium bg-white bg-opacity-10 hover:bg-opacity-20 hover:bg-primary rounded-full transition-all border border-white border-opacity-10"
                  >
                    {{ tag.name }}
                  </button>
                </div>
              </div>
            </div>

            <!-- Advanced Options - Collapsible -->
            <div class="collapse collapse-arrow bg-base-200" :class="{ 'collapse-open': showAdvancedOptions, 'collapse-close': !showAdvancedOptions }">
              <input type="checkbox" v-model="showAdvancedOptions" />
              <div class="collapse-title font-semibold flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Voliteľné nastavenia
              </div>
              <div class="collapse-content">
                <div class="space-y-4 pt-2">
                  <!-- Company -->
                  <div class="form-control">
                    <label class="label">
                      <span class="label-text flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        Firma
                      </span>
                    </label>
                    <div class="join">
                      <radio-button
                        v-for="company in companies"
                        :key="'company' + company.id"
                        :name="company.code"
                        :value="company.id"
                        v-model="createReportData.company_id"
                      />
                    </div>
                  </div>

                  <!-- Type -->
                  <div class="form-control">
                    <label class="label">
                      <span class="label-text flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Typ
                      </span>
                    </label>
                    <div class="join">
                      <radio-button
                        v-for="type in reportTypes"
                        :key="'type' + type.id"
                        :name="type.name"
                        :value="type.id"
                        v-model="createReportData.type_id"
                      />
                    </div>
                  </div>

                  <!-- Location - conditional -->
                  <div class="form-control" v-if="createReportData.type_id == 1">
                    <label class="label">
                      <span class="label-text flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Lokácia
                      </span>
                    </label>
                    <select class="select select-bordered w-full" v-model="createReportData.location_id">
                      <option
                        v-for="location in reportLocations"
                        :label="location.name"
                        :key="location.id"
                        :value="location.id"
                      >
                        {{ location.name }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="card-actions justify-end pt-4 gap-2">
              <button type="button" class="btn btn-ghost" @click="$refs.modal.close()">
                Zrušiť
              </button>
              <button class="btn btn-primary" type="submit" :disabled="lockUpdatingReport || lockCreatingNewReport">
                <span v-if="lockUpdatingReport || lockCreatingNewReport" class="loading loading-spinner"></span>
                {{ isEditingReport ? 'Uložiť' : 'Vytvoriť' }}
              </button>
            </div>
          </form>
        </div>
      </template>
    </modal>
  </view-container>
</template>

<script>
import { request } from '@/api/request'
import dayjs from 'dayjs'
import { chain, cloneDeep, orderBy, sumBy } from 'lodash'
import { mapState } from 'vuex'
import RadioButton from '@/components/RadioButton.vue'
import Autocomplete from '@/components/Autocomplete.vue'
import TagAutocomplete from '@/components/TagAutocomplete.vue'

export default {
  name: 'EmployeeReporting',
  components: { RadioButton, Autocomplete, TagAutocomplete },

  data: () => ({
    loading: 0,
    now: null,

    dayReports: [],
    companies: [],
    reportTypes: [],
    reportLocations: [],
    reportSubjects: [],
    reportTags: [],
    mostUsedMySubjects: [],
    mostUsedMyTags: [],
    selectedDay: null,

    isEditingReport: false,
    isCreatingNewReport: false,
    createReportData: null,

    lockCreatingNewReport: false,
    lockUpdatingReport: false,

    showAdvancedOptions: false,

    emptyReport: {
      hours: '',
      minutes: '',
      company_id: '',
      type_id: '',
      note: '',
      subject_id: '',
      location_id: '',
      tags: []
    }
  }),
  computed: {
    ...mapState({
      employee: (state) => state.employee.employeeData
    }),
    isSameMonth() {
      return dayjs(this.now).isSame(dayjs(), 'month')
    },
    allReports() {
      return this.dayReports.map((dayReport) => dayReport.reports).flat()
    },
    allDuration() {
      return this.sumBy(this.allReports, 'duration')
    },
    summary() {
      if (!this.allReports.length) {
        return []
      }

      return chain(this.allReports)
        .groupBy('company_id')
        .map((value) => {
          const companyDuration = this.sumBy(value, 'duration')

          return {
            company: Object.values(value)[0].company,
            duration: companyDuration,
            percentage: Math.round(companyDuration / this.sumBy(this.allReports, 'duration') * 100) || 0
          }
        })
    }
  },
  watch: {
    'createReportData.type_id': function (newValue, oldValue) {
      if (newValue && this.createReportData.type_id != 1) {
        this.createReportData.location_id = ''
      }

      if (this.createReportData.type_id == 1
        && !this.createReportData.location_id
      ) {
        this.createReportData.location_id = 1
      }
    }
  },
  methods: {
    applySubject(subjectId) {
      this.createReportData.subject_id = subjectId
    },
    applyTag(tagId) {
      if (this.createReportData.tags.find((tag) => tag.id === tagId)) {
        return
      }

      const tag = this.reportTags.find((tag) => tag.id === tagId)
      this.createReportData.tags.push(tag)
    },
    resizeNoteTextarea() {
      let element = this.$refs.note

      if (element.scrollHeight > 74) {
        element.style.height = element.scrollHeight + 2 + 'px'
      }
    },
    addDuration(value) {
      if (!this.createReportData.minutes) {
        this.createReportData.minutes = 0
      }

      if (!this.createReportData.hours) {
        this.createReportData.hours = 0
      }

      // if minutes is more then 60, add to hours but remainder to minutes
      if (value + this.createReportData.minutes >= 60) {
        this.createReportData.hours = parseInt(this.createReportData.hours) + 1
        this.createReportData.minutes = (value + this.createReportData.minutes) - 60
      } else {
        this.createReportData.minutes = parseInt(this.createReportData.minutes) + value
      }
    },
    sumBy(array, key) {
      return sumBy(array, key)
    },
    isToday(date) {
      return dayjs(date).isSame(dayjs(), 'day')
    },
    closeModal() {
      this.selectedDay = null
      this.isEditingReport = this.isCreatingNewReport = false
      this.showAdvancedOptions = false
    },
    createNewReport(day) {
      this.isEditingReport = false
      this.isCreatingNewReport = true
      this.createReportData = cloneDeep(this.emptyReport)

      // Set default company (first one)
      if (this.companies.length > 0) {
        this.createReportData.company_id = this.companies[0].id
      }

      // Set default type (first one)
      if (this.reportTypes.length > 0) {
        this.createReportData.type_id = this.reportTypes[0].id
      }

      // Set default location from employee data
      if (this.employee && this.employee.location_id) {
        this.createReportData.location_id = this.employee.location_id
      } else if (this.reportLocations.length > 0) {
        // Fallback to first location if employee has no default
        this.createReportData.location_id = this.reportLocations[0].id
      }

      this.selectedDay = day
      this.showAdvancedOptions = true // Show advanced options (Type, Company, Location) by default
      this.$refs.modal.open()
    },
    editReport(day, report) {
      this.isCreatingNewReport = false
      this.isEditingReport = true
      this.createReportData = { ...report }
      this.selectedDay = day
      this.showAdvancedOptions = true // Show advanced options when editing
      this.$refs.modal.open()
    },
    formAction() {
      if (this.isEditingReport) {
        this.updateReport()
      } else {
        this.createReport()
      }
    },
    async updateReport() {
      if (
        !(
          this.createReportData.note &&
          this.createReportData.subject_id &&
          this.createReportData.company_id &&
          this.createReportData.type_id
        )
        && (this.createReportData.type_id == 1 && this.createReportData.location_id)
      ) {
        this.$store.dispatch('error/handler', {
          message: 'Vyplňte všetky polia',
          color: 'alert-warning'
        })
        return
      }

      // Validate that total time is not 0
      const totalMinutes = (this.createReportData.hours || 0) * 60 + (this.createReportData.minutes || 0)
      if (totalMinutes === 0) {
        this.$store.dispatch('error/handler', {
          message: 'Zadajte čas práce.',
          color: 'alert-warning'
        })
        return
      }

      this.lockUpdatingReport = true
      const data = await request('updateReport', {
        id: this.createReportData.id,
        date: this.selectedDay.date,
        hours: this.createReportData.hours,
        minutes: this.createReportData.minutes,
        companyId: this.createReportData.company_id,
        typeId: this.createReportData.type_id,
        subjectId: this.createReportData.subject_id,
        tags: this.createReportData.tags.map((tag) => tag.id),
        note: this.createReportData.note,
        locationId: this.createReportData.location_id
      })

      if (data && data.success) {
        await this.loadEmployeeReports()

        this.createReportData = cloneDeep(this.emptyReport)

        this.$store.dispatch('error/handler', {
          message: 'Report bol uložený.',
          color: 'alert-success'
        })
        this.$refs.modal.close()
      } else {
        this.$store.dispatch('error/handler', {
          message: 'Nepodarilo sa uložiť report.',
          color: 'alert-error'
        })
      }

      this.lockUpdatingReport = false
    },
    async createReport() {
      if (
        !(
          this.createReportData.note &&
          this.createReportData.subject_id &&
          this.createReportData.company_id &&
          this.createReportData.type_id
        )
        && (this.createReportData.type_id == 1 && this.createReportData.location_id)
      ) {
        this.$store.dispatch('error/handler', {
          message: 'Vyplňte všetky polia.',
          color: 'alert-warning'
        })
        return
      }

      // Validate that total time is not 0
      const totalMinutes = (this.createReportData.hours || 0) * 60 + (this.createReportData.minutes || 0)
      if (totalMinutes === 0) {
        this.$store.dispatch('error/handler', {
          message: 'Zadajte čas práce.',
          color: 'alert-warning'
        })
        return
      }

      this.lockCreatingNewReport = true
      const data = await request('createReport', {
        date: this.selectedDay.date,
        hours: this.createReportData.hours,
        minutes: this.createReportData.minutes,
        companyId: this.createReportData.company_id,
        typeId: this.createReportData.type_id,
        subjectId: this.createReportData.subject_id,
        tags: this.createReportData.tags.map((tag) => tag.id),
        note: this.createReportData.note,
        locationId: this.createReportData.location_id
      })

      if (data && data.success) {
        await this.loadEmployeeReports()

        this.createReportData = { ...this.emptyReport }

        this.$store.dispatch('error/handler', {
          message: 'Report bol vytvorený.',
          color: 'alert-success'
        })

        this.$refs.modal.close()
      } else {
        this.$store.dispatch('error/handler', {
          message: 'Nepodarilo sa vytvoriť report.',
          color: 'alert-error'
        })
      }

      this.lockCreatingNewReport = false
    },
    formatDatetime(date, format = 'D. M. YYYY HH:mm', locale = 'sk') {
      if (!date) {
        return
      }
      return dayjs(date).locale(locale).format(format)
    },
    formatMinutes(min) {
      if (min <= 0) {
        return
      }
      let hours = min / 60
      let rhours = Math.floor(hours)
      let minutes = (hours - rhours) * 60
      let rminutes = Math.round(minutes)
      return (
        rhours.toString().padStart(2, '0') +
        ':' +
        rminutes.toString().padStart(2, '0')
      )
    },
    sortDayReports(reports) {
      return orderBy(reports, ['created_at'], ['desc'])
    },
    async loadEmployeeReports() {
      const data = await request('getEmployeeReports', {
        'month': this.now.format('M'),
        'year': this.now.format('YYYY')
      })
      if (data && data.success) {
        this.dayReports = orderBy(data.dayReports.filter((report) => dayjs(report.date).isBefore() || dayjs(report.date).isSame()), ['date'], ['desc'])
        this.companies = data.companies
        this.reportTypes = data.reportTypes
        this.reportLocations = data.reportLocations
        this.reportSubjects = data.reportSubjects
        this.reportTags = data.reportTags
        this.mostUsedMySubjects = data.mostUsedMySubjects
        this.mostUsedMyTags = data.mostUsedMyTags
      }
    },
    async goBackwards() {
      this.now = dayjs(this.now).subtract(1, 'months')

      this.loading = 1
      await this.loadEmployeeReports()
      this.loading = 0
    },
    async goForward() {
      this.now = dayjs(this.now).add(1, 'months')

      this.loading = 1
      await this.loadEmployeeReports()
      this.loading = 0
    }
  },
  async created() {
    this.now = dayjs()
    this.loading = 1
    await this.loadEmployeeReports()
    this.loading = 0
  }
}
</script>

<style lang="scss" scoped>
.table {
  width: 100%;

  .row {
    @apply flex;
    @apply flex-col;

    &:first-of-type {
      .row-header {
        .date {
          &:before {
            top: 45%;
          }

          &:after {
            top: 10%;
          }
        }
      }
    }

    .row-header {
      @apply flex;
      @apply justify-between;
      @apply items-center;
      @apply py-6;
      @apply px-4;
      @apply relative;

      &.today {
        @apply pt-0;
        .date {
          &:after {
            @apply bg-primary;
          }
        }
      }

      .date {
        @apply flex;
        @apply items-center;
        @apply font-semibold;
        @apply pl-2;
        @apply min-w-[150px];
        @apply text-base;
        @apply text-white;

        &:before {
          @apply h-[80px];
          @apply bg-neutral;
          @apply absolute;
          @apply w-[2px];
          content: "";
          left: 0px;
          z-index: -1;
        }

        &:after {
          @apply rounded-full;
          @apply bg-neutral-500;
          @apply absolute;
          content: "";
          @apply w-[16px];
          @apply h-[16px];
          left: -7px;
          top: 41%;
        }

        .day-name {
          @apply mr-2;
        }
      }
    }

    .row-reports {
      @apply flex;
      @apply flex-col;
      @apply pl-4;
      @apply border-l-2;
      @apply border-neutral;
    }

    .row-report {
      @apply border-r;
      @apply border-l;
      @apply border-neutral-700;
      @apply p-4;
      @apply flex;
      @apply justify-between;
      @apply border-b;

      .green {
        @apply bg-green-700;
        @apply border-green-700;
        @apply text-white;
      }

      .orange {
        @apply bg-yellow-600;
        @apply border-yellow-600;
        @apply text-white;
      }

      .blue {
        @apply bg-blue-700;
        @apply border-blue-700;
        @apply text-white;
      }

      &:first-of-type {
        @apply rounded-tl-md rounded-tr-md;
        @apply border-t;
      }

      &:last-of-type {
        @apply rounded-bl-md rounded-br-md;
        @apply border-b;
      }

      &:hover {
        @apply bg-neutral-800;
        @apply cursor-pointer;
      }
    }
  }

  .row-footer {
    @apply flex;
    @apply justify-between;
    @apply items-center;
    @apply p-4;
    @apply gap-4;
  }
}
</style>
